import * as React from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { clearApiCache } from "../app/serviceWorkerRegistration";
import * as account from "../data/account";
import { takeInvite, takeRef } from "../data/share";
import User from "../types/User";

// The session itself comes back in HttpOnly cookies (ADR 0014).

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    username: string,
    password: string,
    invite?: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  /** Adopt a user the API just signed in (e.g. after a password reset). */
  completeSignIn: (user: User) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // The cached user renders a returning visit signed in at once, instead
  // of flashing the signed-out screen while /auth/me is in flight.
  const [user, setUserState] = useState<User | null>(account.cachedUser);
  // Only block on /auth/me when there's a session but nothing cached to show.
  const [loading, setLoading] = useState<boolean>(
    () => account.signedIn() && user === null,
  );

  const setUser = (next: User) => {
    account.rememberUser(next);
    setUserState(next);
  };

  const clearSession = () => {
    // The offline copies of this user's API responses go with the session,
    // and so do the ones pages remember in memory.
    account.endSession();
    clearApiCache();
    setUserState(null);
  };

  // Revalidate the session in the background. Only a 401 means it's gone;
  // network errors (e.g. opening the installed app offline) keep the cached
  // session rather than logging the user out.
  useEffect(() => {
    if (!account.signedIn()) {
      return;
    }

    account
      .me()
      .then(setUser)
      .catch((err) => {
        if (account.signedOut(err)) clearSession();
      })
      .finally(() => setLoading(false));
  }, []);

  const completeSignIn = (next: User) => {
    account.beginSession();
    setUser(next);
  };

  const persist = ({ user }: { user: User }) => completeSignIn(user);

  const login = async (email: string, password: string) =>
    persist(await account.login(email, password));

  const signup = async (
    email: string,
    username: string,
    password: string,
    invite?: string,
  ) =>
    persist(
      await account.signup({
        email,
        username,
        password,
        // Whose shared link brought them, if one did (ADR 0016).
        ref: takeRef(),
        // The invite code (plan 003 C.2): typed in, or the link's.
        invite: invite || takeInvite(),
      }),
    );

  // The cookies are HttpOnly, so only the API can clear them. Sign out
  // locally whatever happens: a failed request must not leave the user stuck.
  const logout = async () => {
    try {
      await account.logout();
    } catch {
      // Offline or already signed out; the local state still clears.
    } finally {
      clearSession();
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, setUser, completeSignIn }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
